import React from 'react'
import { Box, Checkbox, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import FileItemCard from './FileItemCard'
import EmptyState from './EmptyState'
import { FileRowSkeleton, FileGridSkeleton } from './Skeleton'
import { FileItem } from '../services/api'

export type ViewMode = 'grid' | 'list'

type Props = {
  files: FileItem[]
  viewMode: ViewMode
  loading?: boolean
  hasError?: boolean // Quando há erro, não mostra EmptyState
  onOpen: (file: FileItem) => void
  onDelete: (fileId: string) => void
  onDownload: (fileId: string) => void
  onUploadClick: () => void
  // Selection props
  selectionMode?: boolean
  selectedFiles?: FileItem[]
  onSelectFile?: (file: FileItem) => void
}

export default function FileList({
  files,
  viewMode,
  loading,
  hasError,
  onOpen,
  onDelete,
  onDownload,
  onUploadClick,
  selectionMode = false,
  selectedFiles = [],
  onSelectFile
}: Props) {
  const selectedIds = new Set(selectedFiles.map(f => f.fileId))

  // Loading state
  if (loading) {
    if (viewMode === 'grid') {
      return (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
              <FileGridSkeleton />
            </Grid>
          ))}
        </Grid>
      )
    }
    return (
      <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <FileRowSkeleton key={i} />
        ))}
      </Box>
    )
  }

  // Empty state - só mostra se não houver erro
  if (!files.length && !hasError) {
    return <EmptyState onUploadClick={onUploadClick} />
  }
  
  // Se não tem arquivos e tem erro, não renderiza nada (o erro é mostrado no App)
  if (!files.length && hasError) {
    return null
  }

  // Grid view
  if (viewMode === 'grid') {
    return (
      <Grid container spacing={2}>
        {files.map(file => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={file.fileId}>
            <FileItemCard
              file={file}
              viewMode={viewMode}
              onOpen={onOpen}
              onDelete={onDelete}
              onDownload={onDownload}
              selectionMode={selectionMode}
              selected={selectedIds.has(file.fileId)}
              onSelect={onSelectFile}
            />
          </Grid>
        ))}
      </Grid>
    )
  }

  // List view
  return (
    <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
      {/* Header da lista */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: '#fafafa'
        }}
      >
        {selectionMode && <Box sx={{ width: 42 }} />}
        <Box sx={{ width: 40 }} />
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, color: '#666' }}>
          Nome
        </Typography>
        <Typography variant="caption" sx={{ width: 140, fontWeight: 600, color: '#666' }}>
          Data
        </Typography>
        <Typography variant="caption" sx={{ width: 80, fontWeight: 600, color: '#666' }}>
          Tamanho
        </Typography>
        <Box sx={{ width: 100 }} />
      </Box>

      {/* Itens da lista */}
      {files.map(file => (
        <FileItemCard
          key={file.fileId}
          file={file}
          viewMode={viewMode}
          onOpen={onOpen}
          onDelete={onDelete}
          onDownload={onDownload}
          selectionMode={selectionMode}
          selected={selectedIds.has(file.fileId)}
          onSelect={onSelectFile}
        />
      ))}
    </Box>
  )
}
